import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from "@angular/core";
import {AbstractListView} from "../../../../core/visbos-core/ui/components/abstract-list-view";
import {SessionActions} from "../../../../shared/actions";
import {DatatableComponent} from "../../../../core/visbos-core/ui/components/datatable/datatable.component";
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {StocktakingService} from "../../../services/stocktaking/stocktaking.service";
import {StocktakingEntry} from "../../../model/stocktaking-entries";
import {AbstractVisbosControl} from "../../../../core/visbos-core/ui/forms/abstract-visbos-control";
import {ProductBatchFormComponent} from "../../../product/product-batch-form/product-batch-form.component";
import {ProductBatch} from "../../../model/product-batch.type";
import {GenericModalComponent} from "../../../../core/visbos-core/ui/forms/generic-modal/generic-modal.component";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import {AbstractVisbosComponent} from "../../../../core/visbos-core/components/abstract-visbos.component";
import {MessageType} from "../../../../shared/redux-store/session";
import {Observable} from "rxjs/Observable";


@Component({
  selector: 'vb-stocktaking-entries',
  templateUrl: './stocktaking-entries.component.html',
  styleUrls: ['./stocktaking-entries.component.css']
})
export class StocktakingEntriesComponent extends AbstractListView implements OnInit, OnChanges {


  @Input()
  autosave: boolean = true;

  @ViewChild('stockDetails')
  articleDetailsTmpl: TemplateRef<any>;

  @ViewChild('editAmountTmpl')
  editAmountTmpl: TemplateRef<any>;

  @ViewChild('batchTmpl')
  batchTmpl: TemplateRef<any>;

  @ViewChild('stocktakingEntriesTable')
  stocktakingEntriesTable: DatatableComponent;

  @ViewChild('pbModal')
  pbModal: GenericModalComponent;

  editable: { [cuid: string]: boolean };

  savingStatusIndicators: Map<string, string> = new Map();

  messages = {
    emptyMessage: 'Keine Posten in der Inventur vorhanden',
    totalMessage: 'Inventur-Posten insgesamt'
  };

  private _stocktakingEntries: StocktakingEntry[] = [];

  get stocktakingEntries(): StocktakingEntry[] {
    return this._stocktakingEntries || [];
  }

  @Input()
  set stocktakingEntries(value: StocktakingEntry[]) {
    this._stocktakingEntries = value;
    this.rows = value;
  }

  @Input()
  control: FormArray;

  pbForm: FormGroup = null;

  @Output()
  savingEntries: EventEmitter<Observable<any>> = new EventEmitter();

  constructor(sessionActions: SessionActions, cd: ChangeDetectorRef,
              private fb: FormBuilder, private stocktakingService: StocktakingService) {

    super(sessionActions, cd);

    this.control = this.fb.array([]);

  }

  ngOnInit() {

    super.ngOnInit();

    this.searchTerm = this.fb.control(null);

    this.dataSubscriptions.push(
      this.searchTerm.valueChanges.pipe(
        distinctUntilChanged(),
        debounceTime(500)
      ).subscribe((term: any) => this.filterItems(term))
    );

    this.columns = [
      {prop: 'stock.depot_yard.label', name: 'Lagerplatz', minWidth: 100, maxWidth: 150, flexGrow: 2},
      {prop: 'label', name: 'Bezeichner', minWidth: 200, flexGrow: 3},
      {prop: 'shall_amount', name: 'Soll', minWidth: 60, maxWidth: 100, flexGrow: 1},
      {prop: 'is_amount', name: 'Ist', cellTemplate: this.editAmountTmpl, minWidth: 60, maxWidth: 100, flexGrow: 1},
      {prop: null, name: 'Chargen', cellTemplate: this.batchTmpl, minWidth: 150, maxWidth: 200, flexGrow: 2},
    ];

  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();

    /*this.cd.detectChanges();

    // Hack for ordering
    let col = this.stocktakingEntriesTable.table._columns[0];
    this.stocktakingEntriesTable.table.onColumnResize({column: col, newValue: 50});

    this.cd.detectChanges();*/

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.control || changes.stocktakingEntries) {

      if (changes.stocktakingEntries.firstChange) {
        this.resetEntryForms();
      }

      for (let entry of this.stocktakingEntries) {
        if (entry.pinned_down) {
          this.getFormGroupByCuid(entry.cuid).disable();
        } else {
          this.getFormGroupByCuid(entry.cuid).enable();
        }
      }

    }
  }

  private resetEntryForms() {
    this.control = this.fb.array([]);
    for (let entry of this.stocktakingEntries) {
      this.control.push(this.initStocktakingEntryFormGroup(entry));
    }
  }

  initStocktakingEntryFormGroup(stocktakingEntry: StocktakingEntry): FormGroup {

    let group = this.fb.group({
      cuid: [stocktakingEntry.cuid || AbstractVisbosControl.cuid()],
      shall_amount: [stocktakingEntry.shall_amount],
      is_amount: [stocktakingEntry.is_amount],
      product_batches: this.initProductBatchesFormArray(stocktakingEntry.pinned_down ? stocktakingEntry.product_batches : (stocktakingEntry.stock ? stocktakingEntry.stock.product_batches : []))
    });

    if (stocktakingEntry.pinned_down) {
      group.disable();
    } else {
      group.enable();
    }

    return group;
  }

  initProductBatchesFormArray(productBatches: ProductBatch[]): FormArray {
    let pbArray: FormArray = this.fb.array([]);
    for (let pb of productBatches) {
      pbArray.push(this.initProductBatchFormGroup(pb));
    }

    return pbArray;
  }

  initProductBatchFormGroup(productBatch?: ProductBatch): FormGroup {
    let f = this.fb.group({
      cuid: [AbstractVisbosControl.cuid(), Validators.required],
      batch_number: [null],
      use_before_date: [null],
      deleted: [false]
    });

    if (productBatch) {
      f.patchValue(productBatch);
    }

    return f;
  }

  getFormGroupByCuid(cuid: string): FormGroup {
    return AbstractVisbosComponent.getFormGroupById(this.control, cuid, 'cuid');
  }

  filterItems(searchTerm) {
    if (!searchTerm || searchTerm == "") {
      this.rows = this.stocktakingEntries;
    } else {
      if (this.stocktakingEntries) {
        this.rows = this.stocktakingEntries.filter((si: any) => this.findStringInTable(si, this.columns, searchTerm));
      }
    }
  }

  onCellUpdate(row, res, index) {
    console.info("Cell update", [row, res, index]);
  }

  markProductBatchAsDeleted(stocktakingEntryCuid: string, productBatchCuid: string) {
    let st = this.getFormGroupByCuid(stocktakingEntryCuid);
    let pb = AbstractVisbosComponent.getFormGroupById(st.get('product_batches') as FormArray, productBatchCuid, 'cuid');
    let oldVal = pb.get('deleted').value;
    pb.patchValue({deleted: !oldVal});
  }

  addProductBatchClicked(stocktakingEntryCuid: string) {
    this.pbModal.state = {stocktakingEntryCuid};
    this.pbForm = ProductBatchFormComponent.initControl(this.fb);
    this.pbModal.show();
  }

  addProductBatch() {
    let productBatch = this.pbForm.value;
    this.pbForm = null;
    let stocktakingEntryCuid = this.pbModal.state.stocktakingEntryCuid;
    let pb = this.initProductBatchFormGroup(productBatch);
    let pbArray = this.getFormGroupByCuid(stocktakingEntryCuid).get('product_batches') as FormArray;
    pbArray.push(pb);
  }

  onItemRemove(item) {

  }

  getRowClass(row) {
    return {
      'pinned-down': row.pinned_down
    };
  }

  pinDownStocktakingEntry(entryCuid: string, data) {
    this.savingStatusIndicators.set(entryCuid, 'saving');
    this.dataSubscriptions.push(this.stocktakingService.saveStocktakingEntry(entryCuid, data).subscribe(res => {
      this.savingStatusIndicators.set(entryCuid, 'success');
    }, err => {
      this.savingStatusIndicators.set(entryCuid, 'error');
      this.sessionActions.throwError(err, "Fehler beim Festschreiben des Inventurpostens!", "Inventur");
    }));

  }

  saveUnsavedEntries() {

    if (this.control.pristine) {
      return;
    }

    let data = [];
    for (let entry of this.rows) {

      let ctrl = this.getFormGroupByCuid(entry.cuid);
      if (!entry.pinned_down && ctrl && ctrl.dirty) {
        data.push(ctrl.value);
      }

    }

    if (!data.length) {
      return;
    }

    let op$ = this.stocktakingService.saveStocktakingEntries(data);
    this.savingEntries.emit(op$);
    op$.subscribe(res => {
      let no = res.data.saveStocktakingEntries.length;
      this.sessionActions.pushMessage(no + " ungespeicherte Inventurposten wurden automatisch gesichert!", MessageType.INFO, "Inventur");
    }, err => {
      this.sessionActions.throwError(err, "Fehler beim automaitschen Speichern der Inventurposten!", "Inventur");
    });

  }

  ngOnDestroy() {
    if (this.autosave) {
      this.saveUnsavedEntries();
    }
    super.ngOnDestroy();
  }
}



// WEBPACK FOOTER //
// ./src/app/store-management/stocktaking/components/stocktaking-entries/stocktaking-entries.component.ts